/*
  Back/Forward Cache is the feature from browsers that save the
  last/next page the user visited to avoid reloading when they
  navigate their browsing history.

  Sometimes though, the cache gets stale (like if they did some
  data change that should be reflected in the last page once they
  hit the back button).

  These utilities are meant to handle those scenarios by allowing
  actions to trigger expireBFCache(), signaling that the cache is
  now stale. The shared setup calls reloadExpiredBFCachePages(), so
  every page loaded before that reloads when restored from BFCache.
  The server expires it on sign-in, sign-up and sign-out.

  --- How to enable Back/Forward Browser Cache in dev for debugging ---

  It's quite hackish but it's the only way I found (besides running the entire
  app in production mode). We effectively disable the websocket connection
  that Vite uses to communicate with the dev server since websockets prevent
  the BF cache behavior.

  Step-by-step:

    > dev exec vite bash
    > apt-get install neovim
    > nvim node_modules/vite/dist/client/client.mjs

    - Look for the createConnection function definitions (there are 2)
    - Replace with something like "false" so it doesn't start a websocket connection

  Now you should be able to simulate the BF Cache in your browser :)
  You can test it in the dev tools "Application" tab, there should be a
  "Back/Forward Cache" item in the Background Services section.

  Remember to re-install the vite dependency afterwards so you restore
  the original behavior in your dev env.
*/

import { addEventListenerWithKey } from "shared/lib/utils"

// Also written by mark_auth_changed (concerns/authentication.rb) on sign-in, sign-up and sign-out.
// A cookie, not storage, so it is readable whenever the user can be signed in at all.
const EXPIRY_COOKIE = "bfcache_expiry"

const readExpiryStamp = () =>
  document.cookie
    .split("; ")
    .find(entry => entry.startsWith(`${EXPIRY_COOKIE}=`))
    ?.slice(EXPIRY_COOKIE.length + 1) ?? null

const stampAtPageLoad = readExpiryStamp()
const isBFCacheExpired = () => readExpiryStamp() !== stampAtPageLoad

// This is for pages that only want to trigger the reload when the cache was explicitly set as stale (by calling expireBFCache)
export const onExpiredBFCacheLoad = (key: string, reloadFunction: () => void) => {
  addEventListenerWithKey(window, "pageshow", key, (event: PageTransitionEvent) => {
    // event.persisted is true when the page loads from BFCache
    if (event.persisted && isBFCacheExpired()) reloadFunction()
  })
}

// This is for pages that want to always reload on BFCache load, regardless of the expiration flag
export const onBFCacheLoad = (key: string, reloadFunction: () => void) => {
  addEventListenerWithKey(window, "pageshow", key, (event: PageTransitionEvent) => {
    // event.persisted is true when the page loads from BFCache
    if (event.persisted) reloadFunction()
  })
}

// Same domain Rails' `domain: :all` picks (".ifttt.com"), so both write one cookie shared across subdomains
const cookieDomain = () => {
  const { hostname } = window.location
  if (!hostname.includes(".") || /^[\d.]+$/.test(hostname)) return ""

  return `; domain=.${hostname.split(".").slice(-2).join(".")}`
}

export const expireBFCache = () => {
  const stamp = `${Date.now()}${Math.random().toString(36).slice(2)}`
  document.cookie = `${EXPIRY_COOKIE}=${stamp}; path=/; samesite=lax${cookieDomain()}`
}

// Reloads any page restored from BFCache after the stamp changed, e.g. a signed-out page after sign-in.
export const reloadExpiredBFCachePages = () => {
  onExpiredBFCacheLoad("expired_bfcache_reload", () => window.location.reload())
}
